import { GridView } from "@mui/icons-material";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography
} from "@mui/material";
import { Menu, Home, Gamepad } from "lucide-react";
import React, { ReactNode, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import StatusToolbar from "./pages/StatusToolbar";

interface LayoutProps {
  children: ReactNode;
}

interface NavigationItem {
  title: string;
  url: string;
  icon: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navigationItems: NavigationItem[] = [
    {
      title: "Home",
      url: "/home",
      icon: <Home fontSize="small" />,
    },
    {
      title: "Board Connect",
      url: "/scanner",
      icon: <GridView fontSize="small" />,
    },
    {
      title: "Chess Connect",
      url: "/connect",
      icon: <Gamepad fontSize="small" />,
    },
  ];

  const drawerContent = (
    <Box height="100%" display="flex" flexDirection="column" sx={{ bgcolor: "background.paper" }}>
      {/* Header */}
      <Toolbar>
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            width={40}
            height={40}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <img
              src="/logo512.png"
              alt="Chess Sense"
              style={{ width: 50, height: 50, objectFit: "contain" }}
            />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Chess Sense
            </Typography>
            <Typography variant="caption" color="primary">
              Your physical Chess companion.
            </Typography>
          </Box>
        </Box>
      </Toolbar>

      {/* Navigation */}
      <Box px={2} py={3}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight="bold"
          textTransform="uppercase"
          gutterBottom
        >
          Navigation
        </Typography>
        <List disablePadding>
          {navigationItems.map((item) => (
            <ListItem key={item.title} disablePadding>
              <ListItemButton
                component={Link}
                to={item.url}
                selected={location.pathname === item.url}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  mb: 1,
                  "&.Mui-selected": {
                    bgcolor: "#dbeafe",
                    color: "#1e40af",
                    boxShadow: 1,
                  },
                }}
                onClick={() => setMobileOpen(false)}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  {item.icon}
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>


      {/* Footer */}
      <Box mt="auto" p={2} borderTop="1px solid #cbd5e1">
        <Box
          display="flex"
          gap={2}
          alignItems="center"
          p={2}
          borderRadius={2}
        >
          <Typography>
            {"Made with <3"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      {/* Sidebar for desktop */}
      <Drawer
        variant="permanent"
        sx={{
          width: 280,
          flexShrink: 0,
          display: { xs: "none", md: "block" },
          [`& .MuiDrawer-paper`]: {
            width: 280,
            boxSizing: "border-box",
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* Sidebar for mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer,
          display: { xs: "block", md: "none" },
          [`& .MuiDrawer-paper`]: {
            width: 280,
            boxSizing: "border-box",
            bgcolor: "white",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{ flexGrow: 1, minHeight: "100vh", overflow: "auto", p: 2 }}
      >
        {/* Mobile Header */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            my: 7 /* height of Toolbar */,
          }}
        />
        <Toolbar
          sx={{
            display: { xs: "flex", md: "none" },
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            alignItems: "center",
            gap: 2,
            px: 3,
            py: 2,
            backdropFilter: "blur(4px)",
            bgcolor: "background.default",
            zIndex: (theme) => theme.zIndex.appBar,
          }}
        >
          <IconButton
            color="primary"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 1 }}
          >
            <Menu />
          </IconButton>
          <Typography variant="h6" fontWeight="bold">
            Smart Chass
          </Typography>
        </Toolbar>

        {/* Page content */}
        <Box p={{ md: 3 }} py={{ xs: 3 }}>
          {/* Status Tool Bar */}
          <StatusToolbar />
          <Toolbar />
          {children}
        </Box>
      </Box>
    </Box>
  );
}
